import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Permissions, PERMISSION_CATALOG } from '@restore/permission-catalog';
//  `./coverage` is a real subpath in the package's `exports` map (tsc and Vite resolve it), but the
//  node import resolver in .eslintrc.json cannot read `exports`, so it reports a false positive.
// eslint-disable-next-line import/no-unresolved
import { recordPermDecision } from '@restore/permission-catalog/coverage';

/**
 * scopeKey-based permission check (S7/THIN-7839).
 *
 * Reads the user's `currentScopes` (scopeKeys) off the profile. During the dual-emit
 * transition (D-14), if the running backend hasn't started emitting `currentScopes` yet,
 * it falls back to matching the legacy human-name `currentPermissions` via the catalog's
 * scopeKey→displayName map — so gating is correct in every environment.
 *
 * Prefer `usePermission(Permissions.READ_PATIENT)` over the human-name string.
 */
export type ScopeKey = keyof typeof Permissions;

/**
 * Permissions the backend already issues but the installed @restore/permission-catalog does
 * not model yet. Without them the human-name fallback below has nothing to match on, so a
 * gate like `has('APPROVE_LABOR_MANAGEMENT')` resolves false for every user — including the
 * DORs who actually hold it.
 *
 * DELETE THIS MAP once the catalog is regenerated with these scopeKeys; the entries below
 * are then supplied by PERMISSION_CATALOG and the spread becomes a no-op.
 */
const PENDING_CATALOG_DISPLAY_NAMES: Record<string, string> = {
  VIEW_TEAM_LABOR: 'View Team Labor',
  EDIT_TEAM_LABOR: 'Edit Team Labor',
  EDIT_OWN_LABOR: 'Edit Own Labor',
  APPROVE_LABOR_MANAGEMENT: 'Approve Labor Management',
  VIEW_LABOR_VALIDATION: 'View Labor Validation',
  EDIT_LABOR_VALIDATION: 'Edit Labor Validation',
  //  BRK-2059: minted in restore-core catalog v31 (BRK-2056); the installed catalog package
  //  predates it. Display string is byte-exact from catalog/permission-catalog.yaml.
  COMPLIANCE_SIGN_ON_BEHALF: 'Compliance Sign On Behalf',
  //  BRK-2073: same v31 mint, same stale-package rationale.
  COMPLIANCE_DOCUMENT_ON_BEHALF: 'Compliance Document On Behalf',
  //  BOPS-294: minted in restore-core catalog v38; same stale-package rationale.
  DOWNLOAD_BILLING_EXPORT: 'Download billing export',
  // THIN-8076 `Apply * Labor Code` rows.
  APPLY_TRNG_LABOR_CODE: 'Apply TRNG Labor Code',
  //  APIT-699: SICK and PTO Payout, minted by the APIT-699 catalog change; the installed catalog
  //  package predates them. Display strings are byte-exact from catalog/permission-catalog.yaml.
  APPLY_SICK_LABOR_CODE: 'Apply SICK Labor Code',
  APPLY_PTOPO_LABOR_CODE: 'Apply PTOPO Labor Code',
  //  THIN-8330: catalog UUID d0c00000-0000-0000-0000-000000000104, group Documentation. Gates
  //  adding an NPC entry on behalf of a team member and editing/deleting a team entry — distinct
  //  from APPROVE_NPC, which only gates the review dashboard itself.
  EDIT_TEAM_NPC: 'Add / Edit Team NPC Log',
  APPROVE_NPC: 'Approve NPC Log',
  //  THIN-8330: a third, separate NPC Log permission from EDIT_TEAM_NPC/APPROVE_NPC above.
  //  UNCONFIRMED — unlike EDIT_TEAM_NPC, neither this scopeKey nor its display name has been
  //  verified against the backend catalog/IamScopeKey yet. If it's wrong, this resolves false
  //  for every user on both branches (scopeKey mismatch, and no display name to fall back to) —
  //  verify and correct before relying on it.
  VIEW_TEAM_NPC: 'View Team NPC Log',
};

/**
 * Extra display names a backend still emits for a scopeKey the catalog *does* model, but under
 * different wording. These cannot go in PENDING_CATALOG_DISPLAY_NAMES above — catalog entries
 * win the spread, so a bridge entry could never override the catalog's wording — so the
 * fallback in checkScope accepts the catalog name *plus* any alias listed here.
 *
 * BOPS-275: the tenant `permissions` table on QA names READ_MONTH_END_ALERT "Access Month End
 * Alerts" (row id 50) while the catalog says "Read Month End Alert". On an environment without
 * dual-emit the human-name branch is the only branch, so the two strings never met and the
 * Monthly Closeout Alerts nav item — plus the prelim review page under it — was hidden from
 * every user regardless of role. One instance of the BOPS-209 namespace mismatch.
 *
 * This widens the match in EVERY environment, prod included, not just the one that diverged.
 * That is safe (an alias only adds a second name for a permission the user must still hold)
 * but it is why the entry must not outlive the mismatch.
 *
 * Aliases are a human-name-fallback concern only; the `currentScopes` branch never consults
 * this map. DELETE the READ_MONTH_END_ALERT entry once the QA `permissions` row is renamed to
 * match the catalog, or once dual-emit ships and that branch stops running — and delete the
 * whole map when it holds no entries. Sibling scope CERTIFY_AND_SIGN_MONTH_END_ALERTS already
 * agrees across catalog and QA table (row id 95), so READ is the only alias needed.
 */
const DISPLAY_NAME_ALIASES: Record<string, string[]> = {
  READ_MONTH_END_ALERT: ['Access Month End Alerts'],
  //  BOPS-298: the same mismatch one level down. QA's tenant rows name these "Access Invoicing"
  //  (id 122) and "Access Contracts" (id 109); the catalog says "Read Invoice" and "Read
  //  Contract". rebill gates the preliminary review on READ_INVOICE, so on an environment
  //  without dual-emit the review was denied to every user regardless of role — the defect
  //  BOPS-298 reported on Stage, which survived the fix on QA for this second reason.
  READ_INVOICE: ['Access Invoicing'],
  READ_CONTRACT: ['Access Contracts'],
  //  RA-2133: this scope has carried THREE display names. It was inserted as "Access MDS
  //  Coordinator Board", the RA-2132 rename changeset (`ra-2132-rename-mds-metrics-permission`)
  //  called it "Access MDS Metrics", and catalog 5.21.0 (v44) calls it "View MDS Metrics" — the
  //  name PERMISSION_CATALOG now supplies. A tenant row only moves to the catalog name when the
  //  5.21.0 seed reconcile runs on it, so until then a tenant may still hold either older name.
  //  On an environment without dual-emit the human-name branch is the only branch, so dropping
  //  either would hide the MDS Metrics nav item and page on every tenant not yet reconciled.
  //  Not consulted when `currentScopes` is present. DELETE once every tenant has run the 5.21.0
  //  seed.
  READ_MDS_COORDINATOR_BOARD: ['Access MDS Metrics', 'Access MDS Coordinator Board'],
  //  BOPS-294: catalog 5.21.0 recapitalises this to "Download Billing Export"; tenant rows were
  //  seeded as "Download billing export" and only move when the 5.21.0 seed reconcile runs.
  //  The match is case-sensitive, so the old wording has to stay accepted until then. DELETE
  //  together with the entry above.
  DOWNLOAD_BILLING_EXPORT: ['Download billing export'],
};

const displayNameByScope: Record<string, string> = PERMISSION_CATALOG.reduce(
  (acc, p) => {
    acc[p.scopeKey] = p.displayName;
    return acc;
  },
  // Catalog entries win, so a regenerated catalog silently supersedes the bridge.
  { ...PENDING_CATALOG_DISPLAY_NAMES } as Record<string, string>
);

function checkScope(profileData: any, scopeKey: string): boolean {
  const scopes = profileData?.currentScopes;
  if (Array.isArray(scopes) && scopes.length > 0) {
    return scopes.includes(scopeKey);
  }
  // Fallback: env still on human-name-only (dual-emit not deployed there yet).
  const names = profileData?.currentPermissions;
  if (!Array.isArray(names)) return false;
  const displayName = displayNameByScope[scopeKey];
  const accepted = [
    ...(displayName ? [displayName] : []),
    ...(DISPLAY_NAME_ALIASES[scopeKey] ?? []),
  ];
  return accepted.some((name) => names.includes(name));
}

export function usePermission(scopeKey: ScopeKey | string): boolean {
  const profileData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );
  const granted = useMemo(
    () => checkScope(profileData, scopeKey as string),
    [profileData, scopeKey]
  );

  // Permission-coverage telemetry (inert unless PERM_TRACE / build key is on). Emitted from an
  // effect, deduped downstream. provider-portal enforces scopes directly, so `enforced` is omitted
  // (defaults to enforce). Shares the enterprise app's schema via @restore/permission-catalog/coverage.
  useEffect(() => {
    recordPermDecision({ scope: scopeKey as string, granted, source: 'usePermission' });
  }, [scopeKey, granted]);

  return granted;
}

/**
 * Returns a `has(scopeKey)` checker for components that evaluate many permissions
 * (e.g. nav config, redirect logic) where per-check hooks would violate the rules of
 * hooks. One selector, memoized; same scopeKey semantics + dual-emit fallback as
 * {@link usePermission}.
 */
export function useScopeChecker(): (scopeKey: ScopeKey | string) => boolean {
  const profileData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );
  return useMemo(
    () => (scopeKey: ScopeKey | string) => {
      const granted = checkScope(profileData, scopeKey as string);
      // Coverage telemetry for nav/route/menu checks (source 'useScopeChecker'). Deduped in the
      // core, so per-render checks over many scopes don't spam. Observational only.
      recordPermDecision({ scope: scopeKey as string, granted, source: 'useScopeChecker' });
      return granted;
    },
    [profileData]
  );
}

export default usePermission;
